import { Component, Input, OnInit, inject } from "@angular/core";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { PlanningSegmentStore } from "@app/services/store/planning-segment-store";
import { AutoBookProfileStore } from "@app/services/store/auto-book-profile-store";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { Store as NgrxStore } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { AutoBookProfile } from "@app/models/rest/auto-book-profile";
import { CyclicalSegmentEditorComponent } from "@app/view/setup/assignment/cyclical-segment-editor/cyclical-segment-editor.component";
import { Column, Model } from "@bryntum/engine-thin";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton } from "@angular/material/button";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";
import { AutobookProfileType } from "@app/models/enums/autobook-profile-type.enum";
import { ClientConfigService } from "@app/core/config/client-config.service";

@Component({
  selector: "app-cyclical-setting-list",
  templateUrl: "./cyclical-setting-list.component.html",
  styleUrls: ["./cyclical-setting-list.component.scss"],
  imports: [MatToolbar, MatButton, GridComponent, MatPaginator, TranslatePipe]
})
export class CyclicalSettingListComponent
  extends BaseDataTablePageDirective<AutoBookProfile>
  implements OnInit
{
  autoBookProfileStore: AutoBookProfileStore;
  segmentStore = inject(PlanningSegmentStore);
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private clientConfigService = inject(ClientConfigService);

  @Input()
  public columns: any[] = [];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const autoBookProfileStore = inject(AutoBookProfileStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      autoBookProfileStore,
      store,
      dialog,
      "AutoBookProfile",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.autoBookProfileStore = autoBookProfileStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;

    this.segmentChooser = this.segmentChooser.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  segmentChooser(data: { record: any }) {
    console.log("item clicked", data);
    let dialogRef = this.dialog.open(CyclicalSegmentEditorComponent, {
      data: {
        profile: data.record.originalData
      }
    });
    dialogRef.afterClosed().subscribe((value) => {
      this.gridStore.load({});
      if (value && value.success) {
        // data.record.backgroundColor = value.backgroundColor;
        // data.record.foregroundColor = value.foregroundColor;
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();

    this.setupColumns();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  private setupColumns() {
    // Standard columns
    const standardColumns: any[] = [
      {
        field: "description",
        text: "Description",
        flex: 1,
        editor: true
      },
      {
        field: "initialPlanningDelay",
        text: "Booking Delay (days)",
        type: "number",
        flex: 1,
        min: 0,
        max: 30
      },
      {
        field: "workingDayLimit",
        text: "Booking Width (days)",
        type: "number",
        flex: 1,
        min: 0,
        max: 30
      }
    ];

    // Only add the profile type column if floating booking is enabled
    if (this.clientConfigService.config.enableFloatingBooking) {
      standardColumns.push({
        field: "type",
        text: "Booking Type",
        flex: 1,
        renderer: ({ value }) => {
          switch (value) {
            case AutobookProfileType.SLOT:
              return "Slot Booking";
            case AutobookProfileType.FLOAT_JOB_WINDOW:
              return "Float - Job Window";
            case AutobookProfileType.FLOAT_PROFILE_WINDOW:
              return "Float - Profile Window";
            default:
              return "Slot Booking";
          }
        },
        editor: {
          type: "dropdown",
          items: [
            { text: "Slot Booking", value: AutobookProfileType.SLOT },
            {
              text: "Float - Job Window",
              value: AutobookProfileType.FLOAT_JOB_WINDOW
            },
            {
              text: "Float - Profile Window",
              value: AutobookProfileType.FLOAT_PROFILE_WINDOW
            }
          ]
        }
      });
    }

    // Segments column
    standardColumns.push(
      {
        field: "instantBook",
        text: "Instant Book",
        type: "check",
        flex: 0.5,
        align: "center"
      },
      {
        text: "Segments",
        type: "template",
        flex: 1,
        id: "segment-column",
        cellMenuItems: [
          {
            text: "Edit Segment List",
            icon: "b-fa b-fa-palette",
            onItem: this.segmentChooser.bind(this)
          }
        ],
        template: ({ record }) => {
          if (!record.segments || record.segments.length === 0) {
            return "None";
          }
          return record.segments.map((segment) => segment.code).join(", ");
        }
      }
    );

    this.columns = standardColumns;
  }

  addRow() {
    this.gridStore.insert(0, {
      description: "",
      initialPlanningDelay: 7,
      workingDayLimit: 14,
      maximumReservationUse: 50,
      type: AutobookProfileType.SLOT,
      instantBook: false,
      segments: []
    });
  }

  onCellClick(event: { record: Model; column: Column }) {
    if (event.column.id === "segment-column") {
      this.segmentChooser({ record: event.record });
    }
  }
}
